import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import moment from 'moment-timezone';
import Swal from 'sweetalert2';

import {
    startGenerarVentaCobroRevision,
    startLoadingCobrosRevision,
    startRefrescarPendientesRevision,
    startResolverCobroRevision,
} from '../../actions/pos';
import {
    AVISO_RESOLVER_NO_MUEVE_DINERO,
    NOMBRE_PANTALLA_REVISION,
    avisoGenerarVenta,
    mensajeGenerarVentaRechazado,
    nombreCompletoPersona,
    puedeRevisarCobros,
} from '../../helpers/cobroDatafono';
import { CobroRevisionFila } from './components/CobroRevisionFila';

/** Formato que espera el backend: `hasta` es inclusivo hasta el final de ese día. */
const FORMATO_FILTRO_FECHA = 'YYYY-MM-DD';

const DIAS_RANGO_POR_DEFECTO = 30;

const NOTA_MAX_CARACTERES = 500;

const TODAS_LAS_SUCURSALES = '';

export const CobrosRevision = () => {
    const dispatch = useDispatch();
    const { role } = useSelector(state => state.auth);
    const { colores, miniLateral } = useSelector(state => state.ui);
    const { sucursales } = useSelector(state => state.sucursales);

    const [cobros, setCobros] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [errorCarga, setErrorCarga] = useState('');
    // Id del cobro con una acción en vuelo: bloquea las DOS salidas de esa fila, porque mientras
    // una petición está en curso la otra ya no puede decir la verdad sobre el cobro.
    const [cobroEnCurso, setCobroEnCurso] = useState('');

    const [sucursal, setSucursal] = useState(TODAS_LAS_SUCURSALES);
    const [desde, setDesde] = useState(
        moment().subtract(DIAS_RANGO_POR_DEFECTO, 'days').format(FORMATO_FILTRO_FECHA));
    const [hasta, setHasta] = useState(moment().format(FORMATO_FILTRO_FECHA));
    const [incluirResueltos, setIncluirResueltos] = useState(false);

    // El `Switch` de rutas del panel no filtra por rol: sin esta guarda se llegaría escribiendo la
    // URL a mano. El backend ya lo rechaza, pero enseñar la pantalla vacía sería peor que no darla.
    const puedeRevisar = puedeRevisarCobros(role);

    const cargar = useCallback(async () => {
        setCargando(true);
        const respuesta = await dispatch(startLoadingCobrosRevision({
            sucursal, desde, hasta, incluirResueltos,
        }));
        setCargando(false);

        if (!respuesta.ok) {
            setCobros([]);
            setErrorCarga(respuesta.msg);
            return;
        }
        setErrorCarga('');
        setCobros(respuesta.cobros);
    }, [dispatch, sucursal, desde, hasta, incluirResueltos]);

    useEffect(() => {
        if (!puedeRevisar) return;
        cargar();
    }, [puedeRevisar, cargar]);

    // La señal del menú cuenta los que quedan sin revisar, que no es lo que muestran los filtros
    // de la pantalla: se refresca aparte para que entrar aquí no la deje desfasada.
    useEffect(() => {
        if (!puedeRevisar) return;
        dispatch(startRefrescarPendientesRevision());
    }, [dispatch, puedeRevisar]);

    /** Las dos salidas piden lo mismo: confirmación que diga qué hace, y una nota opcional. */
    const confirmarConNota = ({ titulo, html, textoConfirmar }) => Swal.fire({
        title: titulo,
        html,
        icon: 'question',
        input: 'textarea',
        inputLabel: 'Qué se ha hecho (opcional)',
        inputAttributes: { maxlength: String(NOTA_MAX_CARACTERES) },
        showCancelButton: true,
        confirmButtonText: textoConfirmar,
        cancelButtonText: 'Cancelar',
    });

    const resolver = async (cobro) => {
        const { isConfirmed, value: nota } = await confirmarConNota({
            titulo: 'Marcar como resuelto',
            html: AVISO_RESOLVER_NO_MUEVE_DINERO,
            textoConfirmar: 'Marcar como resuelto',
        });

        if (!isConfirmed) return;

        setCobroEnCurso(cobro._id);
        const respuesta = await dispatch(startResolverCobroRevision(cobro._id, nota || ''));
        setCobroEnCurso('');

        if (!respuesta.ok) {
            Swal.fire('No se ha podido marcar', respuesta.msg, 'error');
            return;
        }

        Swal.fire(
            'Cobro revisado',
            `Queda constancia de que lo revisó ${nombreCompletoPersona(respuesta.cobro?.resueltoPor)}.`,
            'success',
        );
        cargar();
    };

    /**
     * La otra salida: registrar la venta del cobro con el importe que ya se pasó por la tarjeta.
     *
     * Un fallo NO recarga el listado. El backend revalida stock, saldo y estado de la venta, y
     * cuando rechaza no ha registrado nada: la fila tiene que quedarse donde está, con su motivo
     * delante y la otra salida disponible.
     */
    const generarVenta = async (cobro) => {
        const { isConfirmed, value: nota } = await confirmarConNota({
            titulo: 'Generar la venta',
            html: avisoGenerarVenta(cobro),
            textoConfirmar: 'Generar la venta',
        });

        if (!isConfirmed) return;

        setCobroEnCurso(cobro._id);
        const respuesta = await dispatch(startGenerarVentaCobroRevision(cobro._id, nota || ''));
        setCobroEnCurso('');

        if (!respuesta.ok) {
            Swal.fire('No se ha registrado la venta', mensajeGenerarVentaRechazado(respuesta), 'error');
            return;
        }

        Swal.fire(
            'Venta registrada',
            'La venta del cobro queda registrada y consta que lo revisó '
                + `${nombreCompletoPersona(respuesta.cobro?.resueltoPor)}.`,
            'success',
        );
        cargar();
    };

    if (!puedeRevisar) return <Redirect to="/reservas" />;

    const hayFiltrosAcotando = sucursal !== TODAS_LAS_SUCURSALES;

    return (
        <div
            className="cobros-revision"
            style={{
                '--colorPrincipal': colores.colorPrincipal,
                '--colorSecundario': colores.colorSecundario,
                '--color3': colores.color3,
            }}
        >
            <div className={`preheader ${!miniLateral ? 'compress' : ''}`}>
                <div className="title">
                    <h1 style={{ color: colores.colorPrincipal }}>{NOMBRE_PANTALLA_REVISION}</h1>
                </div>
            </div>

            <p className="cobros-revision__intro">
                Cobros con datáfono en los que la tarjeta se cobró y no quedó ninguna venta
                registrada. Cada uno tiene dos salidas y las eliges tú: generar la venta, que la
                registra con el importe ya cobrado, o marcarlo como resuelto, que solo deja
                constancia de que alguien se ha hecho cargo.
            </p>

            <div className="filters">
                <div className="filter-group">
                    <label htmlFor="cobros-sucursal">LOCAL</label>
                    <select
                        id="cobros-sucursal"
                        value={sucursal}
                        onChange={(e) => setSucursal(e.target.value)}
                    >
                        <option value={TODAS_LAS_SUCURSALES}>Todos los locales</option>
                        {sucursales.map(local => (
                            <option key={local._id} value={local._id}>{local.nombre}</option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label htmlFor="cobros-desde">DESDE</label>
                    <input
                        id="cobros-desde"
                        type="date"
                        value={desde}
                        onChange={(e) => setDesde(e.target.value)}
                    />
                </div>

                <div className="filter-group">
                    <label htmlFor="cobros-hasta">HASTA</label>
                    <input
                        id="cobros-hasta"
                        type="date"
                        value={hasta}
                        onChange={(e) => setHasta(e.target.value)}
                    />
                </div>

                <div className="filter-group">
                    <label htmlFor="cobros-resueltos">REVISADOS</label>
                    <div className="filter-check">
                        <input
                            id="cobros-resueltos"
                            type="checkbox"
                            checked={incluirResueltos}
                            onChange={(e) => setIncluirResueltos(e.target.checked)}
                        />
                        <span>Mostrar los ya revisados</span>
                    </div>
                </div>
            </div>

            {cargando && <p className="cobros-revision__estado">Cargando cobros…</p>}

            {!cargando && errorCarga && (
                <div className="cobros-revision__estado cobros-revision__estado--error">
                    <p>{errorCarga}</p>
                    <button type="button" className="btn-resolver" onClick={cargar}>
                        Volver a intentarlo
                    </button>
                </div>
            )}

            {!cargando && !errorCarga && cobros.length === 0 && (
                <p className="cobros-revision__estado">
                    {hayFiltrosAcotando
                        ? 'No hay cobros pendientes de revisión en ese local y ese rango de '
                            + 'fechas. Prueba a ampliar el periodo o a quitar el filtro de local.'
                        : 'No hay ningún cobro pendiente de revisión en este periodo. Cuando un '
                            + 'cobro con datáfono se quede con la tarjeta cobrada y sin venta '
                            + 'registrada, aparecerá aquí.'}
                </p>
            )}

            {!cargando && !errorCarga && cobros.length > 0 && (
                <div className="cobros-revision__lista">
                    {cobros.map(cobro => (
                        <CobroRevisionFila
                            key={cobro._id}
                            cobro={cobro}
                            onResolver={resolver}
                            onGenerarVenta={generarVenta}
                            accionEnCurso={cobroEnCurso === cobro._id}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
